import CheckIcon from '@mui/icons-material/Check';
import {
  Box,
  DialogContent,
  Grid,
  Stack,
  Avatar,
  Link,
  CircularProgress,
  Dialog,
} from '@mui/material';
import Slide from '@mui/material/Slide';
import { TransitionProps } from '@mui/material/transitions';
import { isEmpty, keys, orderBy } from 'lodash-es';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { AiOutlineExclamationCircle } from 'react-icons/ai';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import {
  getPresignedURLForPut,
  sendBulkEmail,
  IBulkEmailAttachment,
  IJob,
  IBulkEmail,
  getAssignedJobs,
  IJobSearchFilterDTO,
  JobAssignmentStatusFilter,
  getEmailTemplates,
  ITemplateResponse,
  fileUploadToS3,
  ICandidate,
  getUserOrTeamEmailTemplates,
  replacePlaceHolders,
  addCandidateEmail,
  ITemplateDTO,
  getSentEmailLogs,
  IEmailLogPayload,
  IEmailLogDTO,
  acceptWorkAssignment,
  getWorkAssignmentByCurrentRecruiterAndJob,
  getLatestCvOfCandidates,
} from 'wisestep-api';
import {
  WsDialog,
  WsButton,
  WsTypoVariants,
  WsTypography,
  WsDrawer,
  JobSelection,
  IUserDTO,
  WsColors,
  WsTypoColors,
  IWorkAssignment,
  BulkEmailForm,
} from 'wisestep-base';

import { useSKUModalStore } from '../../components/skuModal/SKUModalStore';
import { useUserContext, useWorkspaceContext } from '../../context';
import JobAssignmentSelection from '../../features/conversationWidget/components/JobAssignmentSelection';
import NoSmtpSVG from '../../images/content/NoSMTP.svg';
import { careerPageSettingStore } from '../../utils/careerPageSettingsStore';
import { failed, success, warning } from '../../utils/NotificationMessages';
import { useNotificationStore } from '../../utils/NotificationStore';
import { openUrlInNewTab } from '../../utils/openUrlInNewTab';
import socialMediaShareUrl from '../../utils/shareUrl';
import { useConversationWidgetStore } from '../../utils/useConversationWidgetStore';
import SuccessMessageLayout from '../SuccessMessageLayout';

import { BulkEmailFormNew } from './BulkEmailFormNew';
import {
  replacePlaceHoldersEmailBody,
  useFetchRecruiterSMTPAccounts,
  validateAllEmails,
} from './helper/apiCalls';
import TemplateListing from './Templates/TemplateListing';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});
export interface IEmailFormData {
  attachments: IBulkEmailAttachment[];
  jobCode: string | null;
  job: IJob | null;
  totalCount: number;
  selectedTemplateBody: string;
  selectedTemplateSubject: string;
  selectedTemplateName: string;
  ccList: string[];
  bccList: string[];
  toList?: string[];
}

export interface IApiResponseData {
  jobs: IJob[];
  templates: ITemplateResponse[];
}

export interface IBulkEmailProps {
  candidates: ICandidate[];
  toEmailList?: string[];
  drawer?: boolean;
  ignoreDuplicateCheck?: boolean;
  open?: boolean;
  handleClose: (isClose: boolean) => void;
  defaultJob?: IJob | null;
  emailSuccessMessage?: string;
  amplitudeEventHandler?: (eventType: string) => void;
  recruiterEmail?: string;
  AMTemplate?: ITemplateResponse;
}

interface IJobSelectionState {
  isStateInitialized: boolean;
  tempSelectedJob: IJob | null;
  openJobAssignmentSelection: boolean;
  assignmentData: IWorkAssignment | null;
  assignmentToTeams: string[];
  assignmentByRecruiters: string[];
  assignedJobs: IJob[];
  loader: boolean;
}

export interface ErrorMessage {
  message: string;
  isError: boolean;
  redirectURL?: string;
  redirectionMsg?: string;
  openMessage?: boolean;
}
const BulkEmail = ({
  candidates,
  ignoreDuplicateCheck = false,
  toEmailList = [],
  drawer = true,
  open = false,
  handleClose,
  defaultJob = null,
  emailSuccessMessage = 'Email sent successfully!',
  amplitudeEventHandler,
  recruiterEmail,
  AMTemplate,
}: IBulkEmailProps) => {
  const [state, setState] = useState<IJobSelectionState>({
    isStateInitialized: false,
    tempSelectedJob: null,
    openJobAssignmentSelection: false,
    assignmentData: null,
    assignmentToTeams: [],
    assignmentByRecruiters: [],
    assignedJobs: [],
    loader: false,
  });
  const [openDrawer, setOpenDrawer] = useState(drawer);

  const methods = useForm();

  const [tempJob, setTempJob] = useState<IJob | null>(null);

  const user = useUserContext().userDTO as IUserDTO;

  const careerPageSettings = careerPageSettingStore((state) => state.careerPageSettings);

  const navigate = useNavigate();

  const setModal = useSKUModalStore((state) => state.setModal);

  const accountData = useFetchRecruiterSMTPAccounts();

  const smtpAccounts = accountData.data;
  const orderedSmtpAccounts = !isEmpty(smtpAccounts)
    ? orderBy(smtpAccounts, ['isDefault'], [true]).reverse()
    : [];

  const isLoaded = accountData.isLoaded;

  const onWidgetClose = useConversationWidgetStore((state) => state.onWidgetClose);

  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(true);

  const [emailFormData, setEmailFormData] = useState<IEmailFormData>({
    attachments: [],
    jobCode: null,
    job: defaultJob,
    totalCount: 0,
    selectedTemplateBody: '',
    selectedTemplateSubject: '',
    selectedTemplateName: '',
    ccList: [],
    bccList: [],
  });
  if (toEmailList?.length === 0) {
    toEmailList = candidates.map((candidate) => candidate.email && candidate.email);
  }

  const [apiResponseData, setApiResponseData] = useState<IApiResponseData>({
    jobs: (defaultJob && [defaultJob]) ?? [],
    templates: AMTemplate ? [AMTemplate] : [],
  });

  useEffect(() => {
    AMTemplate &&
      (async () => {
        try {
          setIsDataLoaded(false);
          if (
            AMTemplate?.body?.includes('{{candidateSummary}}') ||
            AMTemplate?.body?.includes('{{candidateCv}}')
          ) {
            const res = await getLatestCvOfCandidates([candidates[0]?.candidateId ?? '']);
            if (res?.data && res.data[0]?.resumeLocation) {
              candidates[0] = {
                ...candidates[0],
                resumeFileOriginalName: res.data[0]?.resumeFileName,
                resumeLocation: res.data[0]?.resumeLocation,
              };
            }
          }
          const { updatedBody, updatedSubject, updatedAttachments } =
            await replacePlaceHoldersEmailBody(AMTemplate, candidates[0], emailFormData.job);
          setEmailFormData((state) => ({
            ...state,
            selectedTemplateBody: updatedBody,
            selectedTemplateSubject: updatedSubject,
            attachments: updatedAttachments,
            ccList: AMTemplate?.cc ?? [],
            bccList: AMTemplate?.bcc ?? [],
          }));
        } catch (error) {
          console.error('Error while processing email template:', error);
        } finally {
          setIsDataLoaded(true);
        }
      })();
  }, [AMTemplate, emailFormData.job, setIsDataLoaded]);

  const workspace = useWorkspaceContext();

  const [openTemplates, setOpenTemplates] = useState<boolean>(false);

  const [openJobs, setOpenJobs] = useState<boolean>(false);

  const [fromEmailSignature, setFromEmailSignature] = useState<string>('');

  const [originalEmailList, setOriginalEmailList] = useState<string[]>([]);

  const setNotification = useNotificationStore((state) => state.setNotification);

  const handleOpenTemplates = () => setOpenTemplates(true);

  const handleCloseTemplates = () => setOpenTemplates(false);

  const [openModal, setOpenModal] = useState(false);

  const [disableSend, setDisableSend] = useState<boolean>(false);

  const [dialogPromise, setDialogPromise] = useState<{
    ok?: (value: boolean | PromiseLike<boolean>) => void;
    fail?: () => void;
  }>({});

  const [duplicateEmail, setDuplicateEmails] = useState<ErrorMessage>({
    message: '',
    isError: false,
    openMessage: false,
  });
  const upload = async (file: File) => {
    let response;
    try {
      response = await getPresignedURLForPut({ fileName: file.name, fileType: file.type });
    } catch (err: any) {
      setNotification(failed(err.message));
    }
    setEmailFormData((prev) => ({
      ...prev,
      attachments: [
        ...prev.attachments,
        {
          attachementName: file.name,
          attachementURL: response.data.preSignedUrl.split('?')[0],
        },
      ],
    }));
    try {
      await fileUploadToS3(file, response.data.preSignedUrl);
      setNotification(success('Uploaded attachment(s) successfully!'));
    } catch (err: any) {
      setNotification(failed(err.message));
    }
  };

  const showConfirmation = async () => {
    setOpenModal(true);
    return new Promise<boolean>((resolve, reject) => {
      setDialogPromise({ ok: resolve, fail: reject });
    });
  };

  const confirm = (value: boolean) => {
    setOpenModal(false);
    if (dialogPromise.ok) dialogPromise.ok(value);
  };

  const send = async (data) => {
    if (data.to.length > 50) {
      setNotification(failed('Please select no more than 50 emails to send email!'));
      return;
    }
    const interactions: string[] = [];
    const sources: string[] = [];
    candidates.map((candidate) => {
      if (candidate.id) {
        interactions.push(candidate.id);
        sources.push(
          candidate.source == '' ? 'NOT_SPECIFIED' : candidate.source ?? 'NOT_SPECIFIED'
        );
      }
    });
    //For single emails only we will update the candidate profile
    //For bulk email we need to design the flow
    if (candidates?.length == 1 && !AMTemplate) {
      setDisableSend(true);
      if (originalEmailList.length == 0) {
        const allowUpdate = await showConfirmation();
        if (!allowUpdate) {
          setDisableSend(false);
          return;
        }
        try {
          await addCandidateEmail({
            id: interactions[0],
            newEmail: data.to[0],
            updatePrimaryEmail: true,
          });
        } catch (error) {
          setDisableSend(false);
        }
      } else if (originalEmailList.length == 1 && data.to.length == 1) {
        if (data.to[0].toLowerCase() !== originalEmailList[0].toLowerCase()) {
          const allowUpdate = await showConfirmation();
          if (!allowUpdate) {
            setDisableSend(false);
            return;
          }
          try {
            await addCandidateEmail({
              id: interactions[0],
              newEmail: data.to[0],
              updatePrimaryEmail: true,
            });
          } catch (error) {
            setDisableSend(false);
          }
        }
      }
      setDisableSend(false);
    }
    const bulkEmailData: IBulkEmail = {
      ...data,
      interactions: [],
      interactionIds: interactions,
      sources: sources,
      downloadSource: candidates[0].downloadSource,
      jobId: emailFormData.job?.id || '',
      jobTitle: emailFormData.job?.title || '',
      job: emailFormData.job,
      jobCode: emailFormData.job?.jobCode || '',
      attachments: emailFormData.attachments,
    };
    bulkEmailData.emailProvider = getProviderByHost(
      smtpAccounts
        .filter((account) => account.fromEmail == bulkEmailData.from)
        .map((data) => data.host)[0]
    );
    const selectedAccount = smtpAccounts.find(
      (account) => account.fromEmail === bulkEmailData.from
    );
    bulkEmailData.smtpAccountId = selectedAccount ? selectedAccount?.id : null;
    bulkEmailData.mailToType = 'BulkEmailToCandidates';
    bulkEmailData.canSendIndividualEmail = false;
    bulkEmailData.recruiterMailBoxIds = [];
    bulkEmailData.ignoreDuplicateCheck = ignoreDuplicateCheck ?? false;

    try {
      setDisableSend(true);
      const response = await sendBulkEmail(bulkEmailData);
      let campaignCode = '';
      let errorMessage = { ...duplicateEmail };
      if (response.data) {
        const duplicateEmails = response.data?.duplicateEmails ?? [];
        campaignCode = response.data?.campaignCode ?? '';
        if (duplicateEmails.length > 0) {
          errorMessage.redirectURL = '/email-settings/logs/sent/';
          errorMessage.redirectionMsg = 'to view the email log';
          if (duplicateEmails.length == data.to.length) {
            errorMessage.message = `Your email is not sent because an earlier email was sent to ${
              duplicateEmails.length == 1 ? 'this candidate' : ' these candidates'
            } for the job ${emailFormData.job?.title} - ${
              emailFormData.job?.jobCode
            } in the last 1 minute `;
            errorMessage.isError = true;
          } else {
            errorMessage.message = 'Your emails are successfully scheduled and processing ';
            errorMessage.isError = false;
          }
          errorMessage.openMessage = true;
          setDuplicateEmails(errorMessage);
        }
      }

      if (!errorMessage.message) {
        if (data.to.length == 1) {
          let emailLogs: IEmailLogDTO = {};
          let count = 0;
          errorMessage.openMessage = true;
          setDuplicateEmails(errorMessage);
          try {
            while (
              (!emailLogs.searchResult?.content ||
                emailLogs.searchResult?.content.length == 0 ||
                !emailLogs.searchResult?.content.length === data.to.length) &&
              count < 10
            ) {
              emailLogs = await getEmailLogs(data.to, campaignCode);
              count = count + 1;
            }
            if (emailLogs.searchResult?.content && emailLogs?.searchResult?.content?.length > 0) {
              const email = emailLogs?.searchResult?.content?.[0];
              if (email && email.isError) {
                const errorDetails = getEmailErrorMessage(email.errors ?? '');
                if (errorDetails) {
                  errorMessage = errorDetails;
                  errorDetails.openMessage = true;
                  setDuplicateEmails(errorDetails);
                }
              }
            } else {
              errorMessage.message =
                'Your email is Queued, Please check email logs after some time ';
              errorMessage.redirectURL = '/email-settings/logs/sent/';
              errorMessage.isError = false;
              setDuplicateEmails(errorMessage);
            }
          } catch (e) {
            setDuplicateEmails({
              message: '',
              isError: false,
              redirectURL: '',
              openMessage: false,
            });
          }
        }
        if (!errorMessage.message) {
          handleClose(false);
          setNotification(success(emailSuccessMessage));
        }
      }
      setEmailFormData({
        attachments: [],
        jobCode: null,
        job: null,
        totalCount: 0,
        selectedTemplateBody: '',
        selectedTemplateSubject: '',
        selectedTemplateName: '',
        bccList: [],
        ccList: [],
      });
      if (!errorMessage.message) {
        handleClose(false);
        setNotification(success(emailSuccessMessage));
      }
      setDisableSend(false);
      if (amplitudeEventHandler) amplitudeEventHandler('Success');
    } catch (err: any) {
      if (err.response.status == 402) {
        setModal({ object: err.response.data, type: 'count' });
      }
      setDisableSend(false);
      setNotification(failed(err.message));
      if (amplitudeEventHandler) amplitudeEventHandler('Failed');
    }
  };

  const openTemplateList = () => {
    handleOpenTemplates();
  };

  const changeJob = () => {
    setEmailFormData((prev) => ({ ...prev, jobCode: null, job: null }));
    setOpenJobs(true);
    getJobs();
  };

  const handleChangeParameters = (query: string, page: number) => getJobs(query, page);

  const getSelectedJob = useCallback(
    (selectedJobCodes: string[]) => {
      setEmailFormData((prev) => ({
        ...prev,
        jobCode: selectedJobCodes[0],
        job: apiResponseData.jobs?.find((job: IJob) => job.jobCode === selectedJobCodes[0]) || null,
      }));
      setOpenJobs(false);
    },
    [apiResponseData.jobs]
  );

  const handleJobSearch = async (data) =>
    getJobs(data.jobSearchQuery, 0, data.numOfResults, data.sortBy);
  useEffect(() => {
    const getAllTemplates = async () => {
      const defaultTemplateResponse = await getEmailTemplates();
      const userOrTeamTemplateResponse = await getUserOrTeamEmailTemplates();
      const allTemplates = [...defaultTemplateResponse.data, ...userOrTeamTemplateResponse.data];
      const favouredTemplates: ITemplateDTO[] = [];
      const unFavouredTemplates: ITemplateDTO[] = [];
      allTemplates.forEach((template: ITemplateDTO) => {
        if (
          user.email &&
          template.favoredRecruitersByTemplate &&
          template?.favoredRecruitersByTemplate?.indexOf(user.email) > -1
        )
          favouredTemplates.push(template);
        else unFavouredTemplates.push(template);
      });
      setApiResponseData((prev) => ({
        ...prev,
        templates: allTemplates,
      }));
    };
    getAllTemplates();
  }, [user.email]);

  const getTemplateId = (selectedTemplateId: string) => {
    apiResponseData.templates.map(async (template) => {
      if (template.id === selectedTemplateId) {
        const bulkEmailData: IBulkEmail = {
          interactions: candidates.length <= 1 ? candidates : [],
          jobId: emailFormData.job?.id ?? '',
          job: emailFormData.job,
          attachments: emailFormData.attachments,
          subject: template.subject ?? '',
          body: template.body ?? '',
          from: recruiterEmail ?? '',
          to:
            toEmailList.length > 0
              ? toEmailList
              : candidates
                  .filter((candidate) => candidate.email)
                  .map((candidate) => candidate.email),
          title: '',
        };
        const response = await replacePlaceHolders(bulkEmailData);
        let jobUrl = '';
        if (emailFormData.job)
          jobUrl = `<a href= "${socialMediaShareUrl(
            emailFormData.job,
            careerPageSettings
          )}" >Apply To Job</a>`;
        setEmailFormData((prev) => ({
          ...prev,
          selectedTemplateName: template.name ?? '',
          selectedTemplateBody: response.data.body.replace('{{job.jobUrl}}', jobUrl) ?? '',
          selectedTemplateSubject:
            response.data.subject?.replace(/<[^>]+>/g, '')?.replace('{{job.jobUrl}}', jobUrl) ?? '',
          ccList: template.cc ?? [],
          bccList: template.bcc ?? [],
        }));
      }
    });
    // handleOpenTemplates();
  };

  const removeSelectedTemplate = () => {
    setEmailFormData((prev) => ({
      ...prev,
      selectedTemplateName: '',
      selectedTemplateBody: '',
      selectedTemplateSubject: '',
    }));
  };

  const getJobs = useCallback(
    async (query = '', page = 0, numOfResults = 10, sortBy = 0) => {
      const jobSearchFilter: IJobSearchFilterDTO = {
        jobAssignmentStatusFilter: JobAssignmentStatusFilter.Assigned_Accepted,
        page: page,
        size: numOfResults,
        query: query,
        sort: sortBy === 0 ? ['postedDate,desc'] : ['postedDate,asc'],
        recruiterPartnerEmail: user?.email,
      };
      try {
        const response = await getAssignedJobs(jobSearchFilter);
        setApiResponseData((prev) => ({ ...prev, jobs: response.data }));
        setEmailFormData((prev) => ({
          ...prev,
          totalCount: parseInt(response.headers['x-total-count']),
        }));
      } catch (err: any) {
        setNotification(failed(err.message));
      }
    },
    [user, setNotification]
  );
  const workspaceContext = useWorkspaceContext();

  const manageSmptAccounts = () =>
    openUrlInNewTab(`https://${workspaceContext.link}.wisestep.com/email-settings/`);

  useEffect(() => {
    defaultJob === null && getJobs();
  }, [defaultJob, getJobs]);

  useEffect(() => {
    if (smtpAccounts) setFromEmailSignature(smtpAccounts[0]?.signatureText ?? '');
  }, [smtpAccounts]);

  useEffect(() => {
    if (candidates?.length == 1) {
      setOriginalEmailList(
        candidates.filter((candidate) => candidate.email).map((candidate) => candidate.email)
      );
    }
  }, [candidates]);

  const onDrawerClose = () => {
    setOpenDrawer(false);
    setEmailFormData({
      attachments: [],
      jobCode: null,
      job: defaultJob,
      totalCount: 0,
      selectedTemplateBody: '',
      selectedTemplateSubject: '',
      selectedTemplateName: '',
      ccList: [],
      bccList: [],
    });
    setDisableSend(false);
    handleClose(false);
    setDuplicateEmails({
      message: '',
      isError: false,
      redirectURL: '',
    });
  };
  const fromEmailSignatureUpdater = (fEmail: string) => {
    const signatureText =
      smtpAccounts
        .filter((account) => account.fromEmail == fEmail)
        .map((data) => data.signatureText)[0] ?? '';
    setFromEmailSignature(signatureText);
  };

  const getEmailLogs = async (candidateEmail: string[], campaignCode: string) => {
    const payload: IEmailLogPayload = {
      page: 0,
      size: 50,
      toEmails: candidateEmail,
      campaignCode: campaignCode,
      // filter: 'FAILED',
    };
    const response = await getSentEmailLogs(payload);
    const logs: IEmailLogDTO = response.data;
    return logs;
  };

  const acceptJobAssignment = useCallback(
    async (assignment: IWorkAssignment, teamIdOrRecruiterId: string | undefined) => {
      try {
        if (teamIdOrRecruiterId) {
          await acceptWorkAssignment(assignment.id, teamIdOrRecruiterId);
          assignment.isAccepted = true;
          setNotification(success('Assignment accepted successfully. You can start working now.'));
          tempJob && tempJob.jobCode && getSelectedJob([tempJob.jobCode]);
          setOpenDrawer(true);
          setState((j) => ({ ...j, tempSelectedJob: null }));
          return true;
        }
        return false;
      } catch (e) {
        console.log(e);
        return false;
      }
    },
    [getSelectedJob, setNotification, tempJob]
  );

  const validAssignment = useCallback(
    async (jobCodes: string[]) => {
      if (!isEmpty(jobCodes)) {
        const job = apiResponseData.jobs.filter((job) => job.jobCode === jobCodes[0])[0];
        setTempJob(job);
        if (job && job.id) {
          const api = await getWorkAssignmentByCurrentRecruiterAndJob(job.id);
          const assignment: IWorkAssignment = api.data;
          if (!isEmpty(api.data)) {
            setState((j) => ({ ...j, assignmentData: assignment }));
            if (!assignment.isClosed) {
              if (!assignment.isAccepted) {
                const assignmentToTeams = keys(assignment.assignedToTeams);
                const assignmentByRecruiters = keys(assignment.assignedByRecruiters);
                setState((j) => ({
                  ...j,
                  assignmentToTeams: assignmentToTeams,
                  assignmentByRecruiters: assignmentByRecruiters,
                }));

                const totalAssignees = assignmentToTeams.length + assignmentByRecruiters.length;
                if (totalAssignees > 1) {
                  setState((j) => ({
                    ...j,
                    tempSelectedJob: job,
                    openJobAssignmentSelection: true,
                  }));
                } else if (totalAssignees == 1) {
                  return assignment?.team?.id
                    ? acceptJobAssignment(assignment, assignment.team?.id)
                    : acceptJobAssignment(assignment, assignmentByRecruiters[0]);
                } else {
                  const teamIdOrRecruiterId = !isEmpty(assignmentToTeams)
                    ? assignmentToTeams[0]
                    : assignmentByRecruiters[0];
                  return acceptJobAssignment(assignment, teamIdOrRecruiterId);
                }
              } else {
                getSelectedJob(jobCodes);
                return true;
              }
            } else {
              const jobOwner = job.team ? job.team.name : 'Job Owner';
              setNotification(
                warning(
                  `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
                )
              );
              onDrawerClose();
              return false;
            }
          } else {
            const jobOwner = job.team ? job.team.name : 'Job Owner';
            setNotification(
              warning(
                `${jobOwner} didn't assign this job to your team.  To add candidates, please get this assigned to your team`
              )
            );
            onDrawerClose();
            return false;
          }
        }
      }
    },
    [acceptJobAssignment, apiResponseData.jobs, getSelectedJob, onDrawerClose, setNotification]
  );

  const validateJob = async (jobCodes: string[]) => {
    const valid = await validAssignment(jobCodes);
    if (valid) {
      getSelectedJob(jobCodes);
    }
  };

  const defaultAttachmentFileNames = useMemo(
    () => emailFormData?.attachments?.map((res) => res?.attachementName) ?? [],
    [emailFormData]
  );
  return (
    <>
      {state.assignmentData !== null && state.openJobAssignmentSelection ? (
        <FormProvider {...methods}>
          <JobAssignmentSelection
            open={state.openJobAssignmentSelection}
            assignment={state.assignmentData}
            assignmentToTeams={state.assignmentToTeams}
            assignmentByRecruiters={state.assignmentByRecruiters}
            acceptJobAssignment={acceptJobAssignment}
            onClose={() => setState((j) => ({ ...j, openJobAssignmentSelection: true }))}
          />
        </FormProvider>
      ) : (
        <>
          {openDrawer ? (
            <WsDrawer
              style={{ zIndex: '1300' }}
              iconType={'cross'}
              title={emailFormData.jobCode === null ? 'Select Job' : 'Send Email'}
              open={open}
              onClose={onDrawerClose}
            >
              {!duplicateEmail.openMessage &&
                emailFormData.jobCode === null &&
                defaultJob === null && (
                  <JobSelection
                    jobs={apiResponseData.jobs}
                    getSelectedJobs={validateJob}
                    handleJobSearch={handleJobSearch}
                    handleChangeParameters={handleChangeParameters}
                    handleClose={() => setEmailFormData((prev) => ({ ...prev, jobCode: null }))}
                    totalCount={emailFormData.totalCount}
                    multiple={false}
                    selectedJobTitle={(v) => console.log(v)}
                  />
                )}
              {emailFormData.job && !duplicateEmail.openMessage && (
                <>
                  {workspace.id === 'c982f84c-d9a8-492a-906b-34a2b1831c3e' ? (
                    <BulkEmailFormNew
                      jobTitle={emailFormData.job?.title ?? ''}
                      jobCode={emailFormData.jobCode ?? emailFormData.job?.jobCode ?? ''}
                      template={emailFormData.selectedTemplateBody}
                      subjectTemplate={emailFormData.selectedTemplateSubject}
                      templateName={emailFormData.selectedTemplateName}
                      fromEmailList={
                        !isEmpty(smtpAccounts)
                          ? orderedSmtpAccounts.map((account) => account.fromEmail)
                          : []
                      }
                      ccList={emailFormData.ccList}
                      bccList={emailFormData.bccList}
                      toEmailList={
                        toEmailList.length > 0
                          ? toEmailList
                          : candidates
                              .filter((candidate) => candidate.email)
                              .map((candidate) => candidate.email)
                      }
                      changeJob={changeJob}
                      manageSenderAccounts={manageSmptAccounts}
                      openTemplatesList={openTemplateList}
                      removeSelectedTemplate={removeSelectedTemplate}
                      uploadAttachment={upload}
                      handleAttachmentError={() =>
                        setNotification(failed('The file size must be less than 25MB!'))
                      }
                      submitAction={send}
                      handleClose={() => handleClose(true)}
                      fromEmailSignature={fromEmailSignature}
                      updateFromEmailSignature={(fEmail) => fromEmailSignatureUpdater(fEmail)}
                      disableSend={disableSend}
                      validateEmails={validateAllEmails}
                      isJobPosterEmail={false}
                      deleteAttachmentHandler={(deletedFileName: string) => {
                        const tempData = emailFormData;
                        tempData.attachments = emailFormData.attachments.filter(
                          (singleAttachment) => singleAttachment.attachementName !== deletedFileName
                        );
                        setEmailFormData(tempData);
                      }}
                      templates={apiResponseData.templates}
                      getTemplateId={getTemplateId}
                    />
                  ) : (
                    <BulkEmailForm
                      jobTitle={emailFormData.job?.title ?? ''}
                      jobCode={emailFormData.jobCode ?? emailFormData.job?.jobCode ?? ''}
                      template={emailFormData.selectedTemplateBody}
                      subjectTemplate={emailFormData.selectedTemplateSubject}
                      templateName={emailFormData.selectedTemplateName}
                      fromEmailList={
                        !isEmpty(smtpAccounts)
                          ? orderedSmtpAccounts.map((account) => account.fromEmail)
                          : []
                      }
                      ccList={emailFormData.ccList}
                      bccList={emailFormData.bccList}
                      toEmailList={
                        toEmailList.length > 0
                          ? toEmailList
                          : candidates
                              .filter((candidate) => candidate.email)
                              .map((candidate) => candidate.email)
                      }
                      changeJob={changeJob}
                      manageSenderAccounts={manageSmptAccounts}
                      openTemplatesList={openTemplateList}
                      removeSelectedTemplate={removeSelectedTemplate}
                      uploadAttachment={upload}
                      handleAttachmentError={() =>
                        setNotification(failed('The file size must be less than 25MB!'))
                      }
                      submitAction={send}
                      handleClose={() => handleClose(true)}
                      fromEmailSignature={fromEmailSignature}
                      updateFromEmailSignature={(fEmail) => fromEmailSignatureUpdater(fEmail)}
                      disableSend={disableSend}
                      validateEmails={validateAllEmails}
                      isJobPosterEmail={false}
                      deleteAttachmentHandler={(deletedFileName: string) => {
                        const tempData = emailFormData;
                        tempData.attachments = emailFormData.attachments.filter(
                          (singleAttachment) => singleAttachment.attachementName !== deletedFileName
                        );
                        setEmailFormData(tempData);
                      }}
                      showImportTemplates={isEmpty(AMTemplate)}
                      defaultAttachmentFileNames={defaultAttachmentFileNames}
                      isLoaded={isDataLoaded}
                    />
                  )}
                </>
              )}
              <WsDrawer
                size="large"
                title={'Select a Template'}
                iconType="cross"
                open={openTemplates}
                onClose={handleCloseTemplates}
                style={{ zIndex: '1302' }}
              >
                <TemplateListing
                  templates={apiResponseData.templates}
                  handleClose={handleCloseTemplates}
                  getTemplateId={getTemplateId}
                />
              </WsDrawer>
              <Dialog
                open={openTemplates}
                TransitionComponent={Transition}
                keepMounted
                onClose={handleClose}
                aria-describedby="alert-dialog-slide-description"
                sx={{ minWidth: '40vw' }}
              >
                <TemplateListing
                  templates={apiResponseData.templates}
                  handleClose={handleCloseTemplates}
                  getTemplateId={getTemplateId}
                />
              </Dialog>
              <WsDialog size="medium" openDialog={openModal}>
                <DialogContent sx={{ p: 2 }}>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    All the conversations with this candidate will now be tagged to this new email
                    address. Would you like to proceed ?
                  </WsTypography>
                  <Grid container>
                    <Grid item xs={10}></Grid>
                    <Grid item xs={1}>
                      <WsButton
                        onClick={() => confirm(false)}
                        type="button"
                        size="large"
                        variant="outlined"
                      >
                        No
                      </WsButton>
                    </Grid>
                    <Grid item xs={1}>
                      <WsButton onClick={() => confirm(true)} type="button" size="large">
                        Yes
                      </WsButton>
                    </Grid>
                  </Grid>
                </DialogContent>
              </WsDialog>
              {duplicateEmail.openMessage && (
                <GetErrorMessage
                  data={duplicateEmail}
                  onClick={() => {
                    onDrawerClose();
                  }}
                  navigate={navigate}
                />
              )}
            </WsDrawer>
          ) : (
            <Box>
              {duplicateEmail.openMessage && (
                <GetErrorMessage
                  data={duplicateEmail}
                  onClick={() => {
                    onDrawerClose();
                    onWidgetClose();
                    // navigate('/email-settings/logs/sent/');
                  }}
                  navigate={navigate}
                />
              )}
              {!duplicateEmail.openMessage && openJobs && (
                <JobSelection
                  jobs={apiResponseData.jobs}
                  getSelectedJobs={getSelectedJob}
                  handleJobSearch={handleJobSearch}
                  handleChangeParameters={handleChangeParameters}
                  handleClose={() => setOpenJobs(false)}
                  totalCount={emailFormData.totalCount}
                  multiple={false}
                  selectedJobTitle={(v) => console.log(v)}
                />
              )}
              {!openJobs &&
                !openTemplates &&
                smtpAccounts.length > 0 &&
                !duplicateEmail.openMessage && (
                  <>
                    {workspace.id === '0ed56aea-54c9-4485-987f-ffb35872eb38' ? (
                      <BulkEmailFormNew
                        jobTitle={emailFormData.job?.title ?? ''}
                        jobCode={emailFormData.jobCode ?? emailFormData.job?.jobCode ?? ''}
                        template={emailFormData.selectedTemplateBody}
                        subjectTemplate={emailFormData.selectedTemplateSubject}
                        templateName={emailFormData.selectedTemplateName}
                        fromEmailList={
                          !isEmpty(smtpAccounts)
                            ? orderedSmtpAccounts.map((account) => account.fromEmail)
                            : []
                        }
                        ccList={emailFormData.ccList}
                        bccList={emailFormData.bccList}
                        toEmailList={
                          toEmailList.length > 0
                            ? toEmailList
                            : candidates
                                .filter((candidate) => candidate.email)
                                .map((candidate) => candidate.email)
                        }
                        changeJob={changeJob}
                        manageSenderAccounts={manageSmptAccounts}
                        openTemplatesList={openTemplateList}
                        removeSelectedTemplate={removeSelectedTemplate}
                        uploadAttachment={upload}
                        handleAttachmentError={() =>
                          setNotification(failed('The file size must be less than 25MB!'))
                        }
                        submitAction={send}
                        handleClose={() => handleClose(true)}
                        fromEmailSignature={fromEmailSignature}
                        updateFromEmailSignature={(fEmail) => fromEmailSignatureUpdater(fEmail)}
                        disableSend={disableSend}
                        validateEmails={validateAllEmails}
                        isJobPosterEmail={false}
                        deleteAttachmentHandler={(deletedFileName: string) => {
                          const tempData = emailFormData;
                          tempData.attachments = emailFormData.attachments.filter(
                            (singleAttachment) =>
                              singleAttachment.attachementName !== deletedFileName
                          );
                          setEmailFormData(tempData);
                        }}
                        templates={apiResponseData.templates ?? []}
                        getTemplateId={getTemplateId}
                      />
                    ) : (
                      <BulkEmailForm
                        jobTitle={emailFormData.job?.title ?? ''}
                        jobCode={emailFormData.jobCode ?? emailFormData.job?.jobCode ?? ''}
                        template={emailFormData.selectedTemplateBody}
                        subjectTemplate={emailFormData.selectedTemplateSubject}
                        templateName={emailFormData.selectedTemplateName}
                        fromEmailList={
                          !isEmpty(smtpAccounts)
                            ? orderedSmtpAccounts.map((account) => account.fromEmail)
                            : []
                        }
                        ccList={emailFormData.ccList}
                        bccList={emailFormData.bccList}
                        toEmailList={
                          toEmailList.length > 0
                            ? toEmailList
                            : candidates
                                .filter((candidate) => candidate.email)
                                .map((candidate) => candidate.email)
                        }
                        changeJob={changeJob}
                        manageSenderAccounts={manageSmptAccounts}
                        openTemplatesList={openTemplateList}
                        removeSelectedTemplate={removeSelectedTemplate}
                        uploadAttachment={upload}
                        handleAttachmentError={() =>
                          setNotification(failed('The file size must be less than 25MB!'))
                        }
                        submitAction={send}
                        handleClose={() => handleClose(true)}
                        fromEmailSignature={fromEmailSignature}
                        updateFromEmailSignature={(fEmail) => fromEmailSignatureUpdater(fEmail)}
                        disableSend={disableSend}
                        validateEmails={validateAllEmails}
                        isJobPosterEmail={false}
                        deleteAttachmentHandler={(deletedFileName: string) => {
                          const tempData = emailFormData;
                          tempData.attachments = emailFormData.attachments.filter(
                            (singleAttachment) =>
                              singleAttachment.attachementName !== deletedFileName
                          );
                          setEmailFormData(tempData);
                        }}
                      />
                    )}
                  </>
                )}
              {isLoaded && smtpAccounts.length <= 0 && (
                <Stack alignItems="center" spacing={4} mt={20}>
                  <Stack alignItems="center">
                    <img src={NoSmtpSVG} alt="No Email ?" height={130} width={140} />
                    <WsTypography bold>Connect your email account and send</WsTypography>
                    <WsTypography bold>emails to single or multiple candidates</WsTypography>
                  </Stack>
                  <WsButton
                    onClick={() => {
                      onWidgetClose();
                      navigate('/email-settings/');
                    }}
                  >
                    Setup email account
                  </WsButton>
                </Stack>
              )}
              {openTemplates && !duplicateEmail.openMessage && (
                <>
                  {workspace.id === '0ed56aea-54c9-4485-987f-ffb35872eb38' ? (
                    <Dialog
                      open={openTemplates}
                      TransitionComponent={Transition}
                      keepMounted
                      onClose={handleClose}
                      aria-describedby="alert-dialog-slide-description"
                    >
                      <TemplateListing
                        templates={apiResponseData.templates}
                        handleClose={handleCloseTemplates}
                        getTemplateId={getTemplateId}
                      />
                    </Dialog>
                  ) : (
                    <TemplateListing
                      templates={apiResponseData.templates}
                      handleClose={handleCloseTemplates}
                      getTemplateId={getTemplateId}
                      width="50vw"
                    />
                  )}
                </>
              )}
              <WsDialog size="medium" openDialog={openModal}>
                <DialogContent sx={{ p: 2 }}>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    All the conversations with this candidate will now be tagged to this new email
                    address. Would you like to proceed ?
                  </WsTypography>
                  <Grid container>
                    <Grid item xs={10}></Grid>
                    <Grid item xs={1}>
                      <WsButton
                        onClick={() => confirm(false)}
                        type="button"
                        size="large"
                        variant="outlined"
                      >
                        No
                      </WsButton>
                    </Grid>
                    <Grid item xs={1}>
                      <WsButton onClick={() => confirm(true)} type="button" size="large">
                        Yes
                      </WsButton>
                    </Grid>
                  </Grid>
                </DialogContent>
              </WsDialog>
            </Box>
          )}
        </>
      )}
    </>
  );
};
export default BulkEmail;

const getProviderByHost = (host) => {
  let provider = 'Custom';
  switch (host) {
    case 'smtp.gmail.com':
      provider = 'gsuite';
      break;
    case 'smtp.outlook.com':
      provider = 'office';
      break;
    case 'smtp.mail.yahoo.com':
      provider = 'yahoo';
      break;
    case 'smtpout.secureserver.net':
      provider = 'godaddyPremium';
      break;
    default:
  }

  return provider;
};

const GetErrorMessage = ({
  data,
  onClick,
  navigate,
}: {
  data: ErrorMessage;
  onClick: () => void;
  navigate: NavigateFunction;
}) => (
  <Grid container display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
    <Grid item xs={9}>
      {!data.message && (
        <Box mr={2}>
          <Grid item xs={12} container justifyContent="center" mt={2}>
            <Box textAlign="center" pt={15}>
              <CircularProgress disableShrink sx={{ color: WsColors.TheBlues500 }} />
            </Box>
          </Grid>
        </Box>
      )}
      {data.message && (
        <SuccessMessageLayout
          message={data.message}
          bold={true}
          icon={
            <Avatar
              sx={{
                backgroundColor: data.isError ? WsColors.Red500 : WsColors.TheBlues400,
                m: 'auto',
              }}
            >
              {data.isError ? <AiOutlineExclamationCircle /> : <CheckIcon />}
            </Avatar>
          }
          button={
            <Grid item xs={12} display="flex" columnGap={1} justifyContent="center">
              <Link
                onClick={() => {
                  onClick();
                  navigate(data.redirectURL ?? '');
                }}
              >
                <WsTypography
                  variant={WsTypoVariants.BODY1}
                  color={WsTypoColors.PRIMARY}
                  cursor="pointer"
                  display="inline"
                >
                  Click here
                </WsTypography>
              </Link>

              <WsTypography
                color={WsTypoColors.SECONDARY}
                variant={WsTypoVariants.BODY1}
                bold={true}
                cursor="pointer"
              >
                {data.redirectionMsg}
              </WsTypography>
            </Grid>
          }
        />
      )}
    </Grid>
  </Grid>
);

const ERROR_MESSAGE = {
  'Quota exceeded': { message: 'Your daily limit has been exceeded.', isError: true },
  'Illegal address': {
    message: "The candidate's email I'd is incorrect.",
    redirectURL: '/email-settings/logs/sent/',
    isError: true,
    redirectionMsg: 'to view the email log',
  },
  'Insufficient Permission': {
    message:
      'You did not provide permissions while configuring smtp, try Re-connecting your account ',
    redirectURL: '/email-settings/',
    isError: true,
    redirectionMsg: 'to reconfigure',
  },
  'NO-REFRESH-TOKEN': {
    message: 'Re-connect your account ',
    redirectURL: '/email-settings/',
    isError: true,
    redirectionMsg: 'to reconfigure',
  },
  'EMPTY-FROM-EMAIL': {
    message: 'Sender email is not correct',
    redirectURL: '/email-settings/logs/sent/',
    isError: true,
    redirectionMsg: 'to view the email log',
  },
  'Duplicate email': {
    message: 'An Email has been sent to this candidate in 1 minute for a job',
    redirectURL: '/email-settings/logs/sent/',
    isError: true,
    redirectionMsg: 'to view the email log',
  },
  ErrorMessageTransientError: {
    message: 'Try After Some time',
    redirectURL: '/email-settings/logs/sent/',
    isError: true,
    redirectionMsg: 'to view the email log',
  },
  AuthenticationFailed: {
    message: 'Re-connect your account ',
    redirectURL: '/email-settings/',
    isError: true,
    redirectionMsg: 'to reconfigure',
  },
};

const getEmailErrorMessage = (errors: string) => {
  const lastIndexOfMessage = errors.lastIndexOf('}');
  const message = errors.substring(errors.indexOf('{'), lastIndexOfMessage + 1);
  let error;
  if (lastIndexOfMessage > -1 && message) {
    const errorMessage = JSON.parse(message).message;
    if (errorMessage.includes('Quota exceed')) {
      error = ERROR_MESSAGE['Quota exceeded'];
    } else {
      error = ERROR_MESSAGE[errors] ?? { value: errorMessage };
    }
  } else if (errors.includes('ErrorMessageTransientError')) {
    error = ERROR_MESSAGE['ErrorMessageTransientError'];
  } else if (errors.includes('Authentication failed')) {
    error = ERROR_MESSAGE['AuthenticationFailed'];
  } else {
    error = ERROR_MESSAGE[errors];
  }
  return error;
};
